import type { StoreObject } from '@apollo/client';
import { defaultDataIdFromObject } from '@apollo/client/cache/inmemory/helpers.js';
import type { KeyFieldsContext } from '@apollo/client/cache/inmemory/policies.js';
import { InMemoryCache } from '@apollo/client/core/core.cjs';
import stringify from 'safe-stable-stringify';
import { z, ZodType } from 'zod';
import { keepNotNull } from '$lib/utils/filterNotNull';
import type { StrictTypedTypePolicies } from '$gql/apollo-helpers.js';
import type { ParticipantInput } from '$gql/graphql.js';
import possibleTypes from '$gql/possible-types.js';
import { matchParticipantInput, zParticipantInput } from '$gql/resolvers/ParticipantRef';
import {
    matchFollowableInput,
    zFollowableInput,
} from '$gql/resolvers/follow/FollowableRef';

function dataIdFromObject(object: Readonly<StoreObject>, context?: KeyFieldsContext) {
    const __typename = object.__typename ?? '';

    if (['InboxThread', 'InboxMessage'].includes(__typename)) {
        const parsed = z
            .object({ id: z.number(), contextId: z.string() })
            .safeParse(object);

        if (parsed.success) {
            return `${__typename}:${parsed.data.id}:${parsed.data.contextId}`;
        }
    }

    return defaultDataIdFromObject(object, context);
}

function createInboxContextId(currentParticipant: ParticipantInput | null) {
    if (currentParticipant === null) {
        return 'inbox-context';
    }
    const { userId, companyId } = currentParticipant;
    if (userId != null) {
        return stringify({ userId });
    } else if (companyId != null) {
        return stringify({ companyId });
    }

    return 'inbox-context';
}

function refineArgs<T, U>(args: Record<string, U> | null, z: ZodType<T>): T | null {
    if (args == null) {
        return null;
    }

    const parsed = z.safeParse(args);

    if (parsed.success) {
        return parsed.data;
    }

    return null;
}

const zWithId = z.object({ id: z.number().or(z.string()) });

export function createCache(): InMemoryCache {
    const typePolicies: StrictTypedTypePolicies = {
        SearchHeader: {
            keyFields: [], // Singleton
        },

        PageNs: {
            keyFields: [], // Singleton,
        },

        Query: {
            fields: {
                crmContract: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;

                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'CrmContract',
                                  id,
                              });
                    },
                },
                notification: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;

                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'Notification',
                                  id,
                              });
                    },
                },
                category: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;

                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'Category',
                                  id,
                              });
                    },
                },
                company: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'Company',
                                  id,
                              });
                    },
                },
                jobOffer: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'JobOffer',
                                  id,
                              });
                    },
                },
                jobTitle: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'JobTitle',
                                  id,
                              });
                    },
                },
                followable: {
                    read(_existing, { args, toReference }) {
                        const safeArgs = refineArgs(
                            args,
                            z.object({ id: zFollowableInput }),
                        );

                        if (safeArgs == null) {
                            return null;
                        }

                        return matchFollowableInput(safeArgs.id, {
                            user: (id) => toReference({ __typename: 'User', id }),
                            company: (id) => toReference({ __typename: 'Company', id }),
                        });
                    },
                },
                participant: {
                    read(_existing, { args, toReference }) {
                        const safeArgs = refineArgs(
                            args,
                            z.object({ participant: zParticipantInput }),
                        );

                        if (safeArgs == null) {
                            return null;
                        }

                        return matchParticipantInput(safeArgs.participant, {
                            user: (id) => toReference({ __typename: 'User', id }),
                            company: (id) => toReference({ __typename: 'Company', id }),
                        });
                    },
                },
                participants: {
                    read(_existing, { args, toReference }) {
                        const safeArgs = refineArgs(
                            args,
                            z.object({ participants: z.array(zParticipantInput) }),
                        );

                        if (safeArgs == null) {
                            return null;
                        }

                        return keepNotNull(
                            safeArgs.participants.map((p) => {
                                return matchParticipantInput(p, {
                                    user: (id) => toReference({ __typename: 'User', id }),
                                    company: (id) =>
                                        toReference({ __typename: 'Company', id }),
                                });
                            }),
                        );
                    },
                },
                cmsPost: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'CmsPost',
                                  id,
                              });
                    },
                },
                media: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'Media',
                                  id,
                              });
                    },
                },
                file: {
                    read(_existing, { args, toReference }) {
                        const id = refineArgs(args, zWithId)?.id ?? null;
                        return id == null
                            ? null
                            : toReference({
                                  __typename: 'File',
                                  id,
                              });
                    },
                },
                inboxThread: {
                    read(_existing, { args, toReference }) {
                        const safeArgs = refineArgs(
                            args,
                            z.object({
                                id: z.number(),
                                currentParticipant: zParticipantInput,
                            }),
                        );

                        const id = safeArgs?.id ?? null;
                        const currentParticipant = safeArgs?.currentParticipant ?? null;

                        return id == null || currentParticipant == null
                            ? null
                            : toReference({
                                  __typename: 'InboxThread',
                                  id,
                                  contextId: createInboxContextId(currentParticipant),
                              });
                    },
                },
                inboxMessage: {
                    read(_existing, { args, toReference }) {
                        const safeArgs = refineArgs(
                            args,
                            z.object({
                                id: z.number(),
                                currentParticipant: zParticipantInput,
                            }),
                        );

                        const id = safeArgs?.id ?? null;
                        const currentParticipant = safeArgs?.currentParticipant ?? null;

                        return id == null || currentParticipant == null
                            ? null
                            : toReference({
                                  __typename: 'InboxMessage',
                                  id,
                                  contextId: createInboxContextId(currentParticipant),
                              });
                    },
                },
            },
        },
    };

    return new InMemoryCache({
        addTypename: true,
        typePolicies: typePolicies,
        dataIdFromObject,
        possibleTypes: possibleTypes.possibleTypes,
    });
}
